// Offers: what a client sells, built from what it is good at (competencies, with proof), with the money
// pages that sell each one.
//
// Competencies live in Client detail → Knowledge; offers in Client detail → Offers. An agent ideates
// offers (an ai_jobs row of kind 'offers' whose queue note is the whole brief; it writes rows through
// scripts/offers.mjs propose, which closes the job) as status 'ideated'. A person moves each to
// 'production' or 'denied'. A money page is a Content Queue row whose offer_id points at a production
// offer; a live page carries client_pages.offer_id (20261009230000_page_offer.sql). Schema:
// supabase/migrations/20261009200000_offers_competencies.sql.

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import type { Database } from './database.types';
import type { JobBrief } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';

export type CompetencyRow = Database['public']['Tables']['client_competencies']['Row'];
export type OfferRow = Database['public']['Tables']['client_offers']['Row'];

export type ProofKind = 'project' | 'review' | 'photo' | 'number' | 'credential' | 'link' | 'note';
export interface Proof {
  kind: ProofKind;
  text: string;
  url?: string;
}

export const PROOF_KINDS: { value: ProofKind; label: string }[] = [
  { value: 'project', label: 'Project' },
  { value: 'review', label: 'Review' },
  { value: 'photo', label: 'Photo' },
  { value: 'number', label: 'Number' },
  { value: 'credential', label: 'Credential' },
  { value: 'link', label: 'Link' },
  { value: 'note', label: 'Note' },
];

export type Competency = Omit<CompetencyRow, 'proof'> & { proof: Proof[] };

export interface DemandSignal {
  query: string;
  volume?: number | null;
  impressions?: number | null;
  position?: number | null;
  source?: string;
}

export type OfferStatus = 'ideated' | 'production' | 'denied';
export type Offer = Omit<OfferRow, 'demand' | 'status'> & { demand: DemandSignal[]; status: OfferStatus };

export const OFFER_STATUS: Record<OfferStatus, { label: string; tone: 'info' | 'up' | 'idle' }> = {
  ideated: { label: 'Ideated', tone: 'info' },
  production: { label: 'Production', tone: 'up' },
  denied: { label: 'Denied', tone: 'idle' },
};

/** A Content Queue row is a money page when it is tied to an offer or is a landing page. */
export const isMoneyPage = (row: { offer_id?: string | null; content_type?: string | null }) =>
  !!row.offer_id || row.content_type === 'landing_page' || row.content_type === 'landing_page_optimise';

const asProof = (v: unknown): Proof[] => (Array.isArray(v) ? (v as Proof[]).filter((p) => p && typeof p.text === 'string') : []);
const toCompetency = (r: CompetencyRow): Competency => ({ ...r, proof: asProof(r.proof) });
const toOffer = (r: OfferRow): Offer => ({ ...r, demand: Array.isArray(r.demand) ? (r.demand as unknown as DemandSignal[]) : [], status: r.status as OfferStatus });

type Json = Database['public']['Tables']['client_offers']['Insert']['demand'];

/* ───── competencies ───── */

export async function fetchCompetencies(clientId: string): Promise<Competency[]> {
  const { data, error } = await supabase
    .from('client_competencies')
    .select('*')
    .eq('client_id', clientId)
    .order('position', { ascending: true, nullsFirst: false })
    .order('created_at');
  if (error) throw error;
  return (data ?? []).map(toCompetency);
}

export async function saveCompetency(
  clientId: string,
  input: { id?: string; name: string; description: string; proof: Proof[] },
  createdBy?: string,
): Promise<Competency> {
  const fields = { name: input.name.trim(), description: input.description.trim(), proof: input.proof as unknown as Json };
  const query = input.id
    ? supabase.from('client_competencies').update(fields).eq('id', input.id)
    : supabase.from('client_competencies').insert({ client_id: clientId, created_by: createdBy ?? null, ...fields });
  // Read back: an RLS-blocked write reports no error, only no row.
  const { data, error } = await query.select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The competency wasn’t saved.');
  return toCompetency(data[0]);
}

export async function deleteCompetency(id: string) {
  const { error } = await supabase.from('client_competencies').delete().eq('id', id);
  if (error) throw error;
}

/* ───── offers ───── */

export async function fetchOffers(clientId: string): Promise<Offer[]> {
  const { data, error } = await supabase
    .from('client_offers')
    .select('*')
    .eq('client_id', clientId)
    .order('position', { ascending: true, nullsFirst: false })
    .order('created_at');
  if (error) throw error;
  return (data ?? []).map(toOffer);
}

export type OfferFields = Pick<Offer, 'name' | 'audience' | 'outcome' | 'mechanism' | 'cta' | 'competency_ids' | 'include_ideas' | 'exclude_ideas'>;

export async function saveOffer(clientId: string, input: OfferFields & { id?: string }, createdBy?: string): Promise<Offer> {
  const fields = {
    name: input.name.trim(),
    audience: input.audience.trim(),
    outcome: input.outcome.trim(),
    mechanism: input.mechanism.trim(),
    cta: input.cta.trim(),
    competency_ids: input.competency_ids,
    include_ideas: input.include_ideas.trim(),
    exclude_ideas: input.exclude_ideas.trim(),
  };
  const query = input.id
    ? supabase.from('client_offers').update(fields).eq('id', input.id)
    : supabase.from('client_offers').insert({ client_id: clientId, created_by: createdBy ?? null, source: 'manual', status: 'production', ...fields });
  const { data, error } = await query.select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The offer wasn’t saved.');
  return toOffer(data[0]);
}

/** Approve (→ production), deny (with a reason), or send back to ideated. */
export async function decideOffer(id: string, status: OfferStatus, decidedBy: string, denyReason?: string): Promise<Offer> {
  const { data, error } = await supabase
    .from('client_offers')
    .update({
      status,
      deny_reason: status === 'denied' ? (denyReason ?? '').trim() || null : null,
      decided_by: status === 'ideated' ? null : decidedBy,
      decided_at: status === 'ideated' ? null : new Date().toISOString(),
    })
    .eq('id', id)
    .select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The offer wasn’t updated.');
  return toOffer(data[0]);
}

export async function deleteOffer(id: string) {
  const { error } = await supabase.from('client_offers').delete().eq('id', id);
  if (error) throw error;
}

/** Content Queue rows tied to any of this client's offers, for the offer detail's money pages. */
export async function fetchOfferPages(clientId: string) {
  const { data, error } = await supabase
    .from('client_content_queue')
    .select('id, title, status, content_type, target_keyword, volume, difficulty, recommended_url, offer_id')
    .eq('client_id', clientId)
    .not('offer_id', 'is', null)
    .order('position', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return data ?? [];
}

/** Live pages (client_pages) tied to any of this client's offers: the money pages already on the site. */
export async function fetchOfferLivePages(clientId: string) {
  const { data, error } = await supabase
    .from('client_pages')
    .select('id, url, title, gsc_clicks, gsc_impressions, gsc_position, template_level, overopt_level, offer_id')
    .eq('client_id', clientId)
    .not('offer_id', 'is', null)
    .order('gsc_impressions', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return data ?? [];
}

/** Tie a live page to an offer (or none). Read back: an RLS-blocked update reports no error. */
export async function setPageOffer(pageId: string, offerId: string | null) {
  const { data, error } = await supabase.from('client_pages').update({ offer_id: offerId }).eq('id', pageId).select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The page wasn’t updated.');
  return data[0];
}

/* ───── ideation job ───── */

function noonTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString();
}

export type OfferIdeationBrief = JobBrief & {
  offer_ideation: { client_id: string; client_name: string; note: string };
};

/**
 * The whole procedure lives in the skill; the note (the "offer-ideation" prompt) names the client, the counts it starts
 * from and the human's steer.
 */
export function formatOfferIdeationNote(
  clientName: string,
  clientId: string,
  note: string,
  counts: { competencies: number; offers: number },
  template: string = PROMPT_DEFAULTS['offer-ideation'],
): string {
  return renderPrompt(template, {
    client: clientName,
    client_id: clientId,
    competencies: String(counts.competencies),
    offers: String(counts.offers),
    steer: note.trim() || '(none)',
  });
}

export async function enqueueOfferIdeation(input: {
  client: { id: string; name: string };
  note: string;
  createdBy: { id: string; name: string };
}): Promise<AiJob> {
  const { client, createdBy } = input;
  const [competencies, offers, template] = await Promise.all([fetchCompetencies(client.id), fetchOffers(client.id), loadPromptTemplate('offer-ideation')]);
  const title = `Offer ideation — ${client.name}`;
  const queueNote = formatOfferIdeationNote(client.name, client.id, input.note, { competencies: competencies.length, offers: offers.length }, template);

  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: client.id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const brief: OfferIdeationBrief = { offer_ideation: { client_id: client.id, client_name: client.name, note: input.note.trim() } };
  return enqueueAiJob({ task_id: task.id, title, card_id: null, created_by: createdBy.id, queue_note: queueNote, kind: 'offers', brief });
}
